import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { mockGoodsReceiptService } from '@/services/mock/MockGoodsReceiptService';
import { mockPurchaseOrderService } from '@/services/mock/MockPurchaseOrderService';
import { CreateGrnInput, CreateGrnItemInput, RejectionReasonCode } from '@/types/domain/grn-management';
import { Phase17GrnHandoffPayload, PurchaseOrderRecord } from '@/types/domain/po-management';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import {
  PackageCheck,
  Truck,
  ArrowLeft,
  CheckCircle2,
  AlertTriangle,
  Building2,
  RefreshCw,
  Search,
} from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import { calculateAcceptedQuantity, calculatePendingQuantity } from '@/features/grn/utils/grnCalculations';

export const GrnCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryPoId = searchParams.get('poId');

  const locationState = location.state as {
    poHandoffPayload?: Phase17GrnHandoffPayload;
    poNumber?: string;
    poId?: string;
  } | null;

  const [availablePos, setAvailablePos] = useState<PurchaseOrderRecord[]>([]);
  const [isLoadingPos, setIsLoadingPos] = useState(false);

  const [formData, setFormData] = useState<CreateGrnInput>({
    poId: '',
    poNumber: '',
    sapPoNumber: '',
    vendorId: '',
    vendorName: '',
    vendorCode: '',
    plantCode: 'PLANT-1000',
    plantName: 'Austin High-Tech Assembly Plant 1000',
    storageLocation: 'SLOC-RAW-01',
    unloadingDock: 'Bay-1',
    deliveryChallanNumber: `DC-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`,
    carrierName: 'FedEx Express Freight',
    trackingNumber: `TRK-${Math.floor(10000000 + Math.random() * 90000000)}`,
    receivedDate: new Date().toISOString().slice(0, 10),
    inspectorName: 'Quality Lead John Miller',
    notes: 'Standard dock receiving and verification against PO.',
    items: [],
  });

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load available POs for dropdown
  useEffect(() => {
    const loadPos = async () => {
      try {
        setIsLoadingPos(true);
        const res = await mockPurchaseOrderService.getPurchaseOrders({ page: 1, pageSize: 50, status: 'ALL', sapStatus: 'ALL', plant: 'ALL', buyer: 'ALL' });
        setAvailablePos(res.items);

        // Pre-fill if handoff provided from Phase 16
        if (locationState?.poHandoffPayload) {
          const handoff = locationState.poHandoffPayload;
          setFormData((prev) => ({
            ...prev,
            poId: handoff.poId,
            poNumber: handoff.poNumber,
            sapPoNumber: handoff.sapPoNumber,
            vendorId: handoff.vendorId,
            vendorName: handoff.vendorName,
            plantCode: handoff.plant,
            items: handoff.items.map((it, idx) => ({
              poItemId: `item-${handoff.poId}-${it.lineNumber}`,
              lineNumber: it.lineNumber,
              materialCode: it.materialCode,
              description: it.description,
              orderedQuantity: it.quantityOrdered,
              receivedQuantity: it.quantityPending > 0 ? it.quantityPending : it.quantityOrdered,
              rejectedQuantity: 0,
              uom: it.uom,
              storageLocation: 'SLOC-RAW-01',
              batchNumber: `BATCH-2026-${100 + idx}`,
              unitPrice: 500,
              taxPercent: 18,
            })),
          }));
        } else if (locationState?.poId || queryPoId) {
          const targetId = locationState?.poId || queryPoId!;
          const po = await mockPurchaseOrderService.getPurchaseOrderById(targetId);
          if (po) {
            handleSelectPo(po);
          }
        }
      } finally {
        setIsLoadingPos(false);
      }
    };

    loadPos();
  }, [locationState, queryPoId]);

  const handleSelectPo = (po: PurchaseOrderRecord) => {
    setFormData((prev) => ({
      ...prev,
      poId: po.id,
      poNumber: po.poNumber,
      sapPoNumber: po.sapPoNumber,
      vendorId: po.vendorId,
      vendorName: po.vendorName,
      vendorCode: po.vendorCode,
      plantCode: po.plant,
      items: po.items.map((it) => ({
        poItemId: it.id,
        lineNumber: it.lineNumber,
        materialCode: it.materialCode,
        description: it.description,
        orderedQuantity: it.quantityOrdered,
        receivedQuantity: it.quantityPending > 0 ? it.quantityPending : it.quantityOrdered,
        rejectedQuantity: 0,
        uom: it.uom,
        storageLocation: it.storageLocation || 'SLOC-RAW-01',
        batchNumber: `BATCH-2026-${Math.floor(100 + Math.random() * 900)}`,
        unitPrice: it.unitPrice,
        taxPercent: it.taxPercent,
      })),
    }));
  };

  const handleItemQtyChange = (index: number, field: 'receivedQuantity' | 'rejectedQuantity', val: number) => {
    const safeVal = Math.max(0, val);
    setFormData((prev) => {
      const updated = [...prev.items];
      const target = { ...updated[index]!, [field]: safeVal };
      if (target.rejectedQuantity > target.receivedQuantity) {
        setErrorMessage(`Rejected quantity cannot exceed received quantity on line ${index + 1}.`);
      } else {
        setErrorMessage(null);
      }
      updated[index] = target;
      return { ...prev, items: updated };
    });
  };

  const handleItemChange = <K extends keyof CreateGrnItemInput>(
    index: number,
    field: K,
    value: CreateGrnItemInput[K]
  ) => {
    setFormData((prev) => {
      const updated = [...prev.items];
      updated[index] = { ...updated[index]!, [field]: value };
      return { ...prev, items: updated };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.poNumber) {
      setErrorMessage('Please select a valid Purchase Order.');
      return;
    }

    if (formData.items.length === 0) {
      setErrorMessage('No line items found for this receipt.');
      return;
    }

    for (let i = 0; i < formData.items.length; i++) {
      const it = formData.items[i]!;
      if (it.receivedQuantity <= 0) {
        setErrorMessage(`Line ${i + 1} (${it.materialCode}): Received quantity must be greater than zero.`);
        return;
      }
      if (it.rejectedQuantity > it.receivedQuantity) {
        setErrorMessage(`Line ${i + 1} (${it.materialCode}): Rejected quantity (${it.rejectedQuantity}) cannot exceed received quantity (${it.receivedQuantity}).`);
        return;
      }
      if (it.rejectedQuantity > 0 && !it.rejectionReason) {
        setErrorMessage(`Line ${i + 1} (${it.materialCode}): Rejection reason is required for rejected units.`);
        return;
      }
    }

    try {
      setIsSubmitting(true);
      setErrorMessage(null);
      const idempotencyKey = `grn-create-${formData.poNumber}-${Date.now()}`;
      const created = await mockGoodsReceiptService.createGoodsReceipt(formData, idempotencyKey);
      navigate(ROUTES.GRN.DETAILS(created.id));
    } catch (err: unknown) {
      setErrorMessage((err as Error).message || 'Failed to create Goods Receipt.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 pb-24 font-heading max-w-5xl mx-auto">
      {/* Top Header */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(ROUTES.GRN.LIST)}
          className="rounded-xl font-bold border-border text-xs"
        >
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Goods Receipts
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="p-3 rounded-2xl bg-primary/10 text-primary">
          <PackageCheck className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-2xl font-black tracking-tight text-foreground">
            Warehouse Material Inwarding & Goods Receipt (GRN)
          </h1>
          <p className="text-xs text-foreground-secondary font-medium mt-0.5">
            Record physical dock delivery, inspect received units, and generate an authoritative SAP MIGO Movement 101 voucher.
          </p>
        </div>
      </div>

      {errorMessage && (
        <div className="p-4 rounded-2xl border border-rose-500/30 bg-rose-500/10 text-rose-800 text-xs font-bold flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 shrink-0 text-rose-600" />
          <span>{errorMessage}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Step 1: PO Selection */}
        <Card className="rounded-2xl border-border bg-card shadow-2xs">
          <CardContent className="p-6 space-y-4">
            <h3 className="text-sm font-extrabold text-foreground flex items-center gap-2">
              <Search className="h-4 w-4 text-primary" /> 1. Select Purchase Order for Inwarding
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
              <div className="space-y-1.5">
                <label className="font-bold text-foreground">Source Purchase Order *</label>
                <select
                  value={formData.poId}
                  onChange={(e) => {
                    const po = availablePos.find((p) => p.id === e.target.value);
                    if (po) handleSelectPo(po);
                  }}
                  className="w-full h-10 px-3 rounded-xl border border-border bg-surface text-foreground font-semibold text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">{isLoadingPos ? 'Loading Purchase Orders...' : '-- Choose Purchase Order --'}</option>
                  {availablePos.map((po) => (
                    <option key={po.id} value={po.id}>
                      {po.poNumber} — {po.vendorName} ({po.plant})
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="font-bold text-foreground-secondary">Vendor / Supplier</label>
                <input
                  type="text"
                  readOnly
                  value={formData.vendorName ? `${formData.vendorName} (${formData.vendorCode})` : 'Auto-populated'}
                  className="w-full h-10 px-3 rounded-xl border border-border bg-surface/50 text-foreground text-xs font-semibold"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 2: Logistics & Dock Details */}
        <Card className="rounded-2xl border-border bg-card shadow-2xs">
          <CardContent className="p-6 space-y-4">
            <h3 className="text-sm font-extrabold text-foreground flex items-center gap-2">
              <Truck className="h-4 w-4 text-primary" /> 2. Delivery Logistics & Unloading Details
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
              <div className="space-y-1.5">
                <label className="font-bold text-foreground">Delivery Challan / DC # *</label>
                <input
                  type="text"
                  required
                  value={formData.deliveryChallanNumber}
                  onChange={(e) => setFormData((prev) => ({ ...prev, deliveryChallanNumber: e.target.value }))}
                  className="w-full h-10 px-3 rounded-xl border border-border bg-surface text-foreground font-mono font-bold text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              <div className="space-y-1.5">
                <label className="font-bold text-foreground">Carrier / Logistics Transporter</label>
                <input
                  type="text"
                  value={formData.carrierName}
                  onChange={(e) => setFormData((prev) => ({ ...prev, carrierName: e.target.value }))}
                  className="w-full h-10 px-3 rounded-xl border border-border bg-surface text-foreground text-xs font-medium focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              <div className="space-y-1.5">
                <label className="font-bold text-foreground">Air Waybill / Tracking #</label>
                <input
                  type="text"
                  value={formData.trackingNumber}
                  onChange={(e) => setFormData((prev) => ({ ...prev, trackingNumber: e.target.value }))}
                  className="w-full h-10 px-3 rounded-xl border border-border bg-surface text-foreground font-mono text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              <div className="space-y-1.5">
                <label className="font-bold text-foreground">Receiving Plant *</label>
                <input
                  type="text"
                  readOnly
                  value={formData.plantCode}
                  className="w-full h-10 px-3 rounded-xl border border-border bg-surface/50 text-foreground font-mono font-bold text-xs"
                />
              </div>

              <div className="space-y-1.5">
                <label className="font-bold text-foreground">Target Storage Location (SLoc) *</label>
                <select
                  value={formData.storageLocation}
                  onChange={(e) => setFormData((prev) => ({ ...prev, storageLocation: e.target.value }))}
                  className="w-full h-10 px-3 rounded-xl border border-border bg-surface text-foreground text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="SLOC-RAW-01">SLOC-RAW-01 (Raw Material Central)</option>
                  <option value="SLOC-MAIN-02">SLOC-MAIN-02 (Main Production Staging)</option>
                  <option value="SLOC-QUAR-03">SLOC-QUAR-03 (Quality Quarantine Hold)</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="font-bold text-foreground">Unloading Bay / Dock</label>
                <input
                  type="text"
                  value={formData.unloadingDock}
                  onChange={(e) => setFormData((prev) => ({ ...prev, unloadingDock: e.target.value }))}
                  className="w-full h-10 px-3 rounded-xl border border-border bg-surface text-foreground text-xs font-medium focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 3: Line Items Receiving & Inspection */}
        <Card className="rounded-2xl border-border bg-card shadow-2xs">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-border/60 pb-3">
              <h3 className="text-sm font-extrabold text-foreground flex items-center gap-2">
                <Building2 className="h-4 w-4 text-primary" /> 3. Line Items Receipt & Inward Counting
              </h3>
              <span className="text-xs text-foreground-secondary font-bold">
                {formData.items.length} Lines to Receive
              </span>
            </div>

            {formData.items.length === 0 ? (
              <div className="p-8 text-center text-xs text-foreground-muted font-medium">
                Please select a Purchase Order above to populate expected line items.
              </div>
            ) : (
              <div className="space-y-4">
                {formData.items.map((it, idx) => {
                  const accepted = calculateAcceptedQuantity(it.receivedQuantity, it.rejectedQuantity);
                  const pending = calculatePendingQuantity(it.orderedQuantity, accepted);

                  return (
                    <div
                      key={it.poItemId || idx}
                      className="p-4 rounded-xl border border-border/80 bg-surface/40 space-y-3"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-border/40 pb-2">
                        <div>
                          <span className="text-xs font-black text-foreground">{it.description}</span>
                          <span className="text-[10px] text-foreground-muted font-mono block">
                            SKU: {it.materialCode} | Ordered: {it.orderedQuantity} {it.uom} | Pending: {pending} {it.uom}
                          </span>
                        </div>
                        <div className="text-xs font-bold text-foreground">
                          Line #{it.lineNumber}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 text-xs">
                        <div>
                          <label className="block text-[10px] font-bold text-foreground mb-1">
                            Physical Received Qty *
                          </label>
                          <input
                            type="number"
                            min={1}
                            value={it.receivedQuantity}
                            onChange={(e) => handleItemQtyChange(idx, 'receivedQuantity', Number(e.target.value))}
                            className="w-full h-9 px-3 rounded-xl border border-border bg-card text-foreground font-mono font-bold text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                          />
                        </div>

                        <div>
                          <label className="block text-[10px] font-bold text-rose-600 mb-1">
                            Rejected Qty
                          </label>
                          <input
                            type="number"
                            min={0}
                            max={it.receivedQuantity}
                            value={it.rejectedQuantity}
                            onChange={(e) => handleItemQtyChange(idx, 'rejectedQuantity', Number(e.target.value))}
                            className="w-full h-9 px-3 rounded-xl border border-border bg-card text-rose-600 font-mono font-bold text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                          />
                        </div>

                        <div>
                          <label className="block text-[10px] font-bold text-emerald-600 mb-1">
                            Net Accepted Qty
                          </label>
                          <div className="h-9 px-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5 text-emerald-700 font-mono font-black text-xs flex items-center">
                            {accepted} {it.uom}
                          </div>
                        </div>

                        <div>
                          <label className="block text-[10px] font-bold text-foreground-secondary mb-1">
                            Batch / Lot #
                          </label>
                          <input
                            type="text"
                            value={it.batchNumber || ''}
                            onChange={(e) => handleItemChange(idx, 'batchNumber', e.target.value)}
                            placeholder="e.g. BATCH-2026-01"
                            className="w-full h-9 px-3 rounded-xl border border-border bg-card text-foreground font-mono text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                          />
                        </div>

                        {it.rejectedQuantity > 0 && (
                          <>
                            <div className="sm:col-span-2">
                              <label className="block text-[10px] font-bold text-rose-600 mb-1">
                                Rejection Justification *
                              </label>
                              <select
                                required
                                value={it.rejectionReason || ''}
                                onChange={(e) => handleItemChange(idx, 'rejectionReason', e.target.value as RejectionReasonCode)}
                                className="w-full h-9 px-3 rounded-xl border border-rose-500/40 bg-card text-rose-700 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-rose-500"
                              >
                                <option value="">Select Reason</option>
                                <option value="DAMAGED">Physical Damage in Transit</option>
                                <option value="QUALITY_FAILURE">Quality Failure (Out of Tolerance)</option>
                                <option value="WRONG_MATERIAL">Wrong SKU / Specification</option>
                                <option value="WRONG_QUANTITY">Quantity Discrepancy</option>
                                <option value="PACKAGING_ISSUE">Packaging / Seal Defect</option>
                                <option value="OTHER">Other Issue</option>
                              </select>
                            </div>

                            <div className="sm:col-span-2">
                              <label className="block text-[10px] font-bold text-foreground-secondary mb-1">
                                Rejection Notes
                              </label>
                              <input
                                type="text"
                                value={it.rejectionNotes || ''}
                                onChange={(e) => handleItemChange(idx, 'rejectionNotes', e.target.value)}
                                placeholder="Describe inspection defect..."
                                className="w-full h-9 px-3 rounded-xl border border-border bg-card text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(ROUTES.GRN.LIST)}
            className="rounded-xl font-bold"
          >
            Cancel
          </Button>

          <Button
            type="submit"
            disabled={isSubmitting || formData.items.length === 0}
            className="rounded-xl font-bold bg-primary hover:bg-primary/90 text-white shadow-2xs"
          >
            {isSubmitting ? (
              <span className="flex items-center gap-1.5">
                <RefreshCw className="h-4 w-4 animate-spin" /> Inwarding Goods Receipt...
              </span>
            ) : (
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4" /> Post Goods Receipt (GRN)
              </span>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};
